import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============== AUTH API ==============
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  updateMe: async (userData) => {
    const response = await api.put('/auth/me', userData);
    return response.data;
  },
};

// ============== GLUCOSE API ==============
export const glucoseAPI = {
  create: async (logData) => {
    const response = await api.post('/glucose', logData);
    return response.data;
  },
  
  getAll: async (limit = 50, skip = 0) => {
    const response = await api.get(`/glucose?limit=${limit}&skip=${skip}`);
    return response.data;
  },
  
  getOne: async (logId) => {
    const response = await api.get(`/glucose/${logId}`);
    return response.data;
  },
  
  delete: async (logId) => {
    const response = await api.delete(`/glucose/${logId}`);
    return response.data;
  },
};

// ============== MEALS API ==============
export const mealsAPI = {
  create: async (mealData) => {
    const response = await api.post('/meals', mealData);
    return response.data;
  },
  
  getAll: async (limit = 50, skip = 0) => {
    const response = await api.get(`/meals?limit=${limit}&skip=${skip}`);
    return response.data;
  },
  
  delete: async (mealId) => {
    const response = await api.delete(`/meals/${mealId}`);
    return response.data;
  },
};

// ============== MEDICATIONS API ==============
export const medicationsAPI = {
  create: async (medData) => {
    const response = await api.post('/medications', medData);
    return response.data;
  },
  
  getAll: async () => {
    const response = await api.get('/medications');
    return response.data;
  },
  
  update: async (medId, updateData) => {
    const response = await api.put(`/medications/${medId}`, updateData);
    return response.data;
  },
  
  delete: async (medId) => {
    const response = await api.delete(`/medications/${medId}`);
    return response.data;
  },
  
  resetDaily: async () => {
    const response = await api.post('/medications/reset-daily');
    return response.data;
  },
};

// ============== RECIPES API ==============
export const recipesAPI = {
  getAll: async (mealType = null, difficulty = null, limit = 50, skip = 0) => {
    let url = `/recipes?limit=${limit}&skip=${skip}`;
    if (mealType) url += `&meal_type=${mealType}`;
    if (difficulty) url += `&difficulty=${difficulty}`;
    const response = await api.get(url);
    return response.data;
  },
  
  getOne: async (recipeId) => {
    const response = await api.get(`/recipes/${recipeId}`);
    return response.data;
  },
};

// ============== EXERCISES API ==============
export const exercisesAPI = {
  create: async (exerciseData) => {
    const response = await api.post('/exercises', exerciseData);
    return response.data;
  },
  
  getAll: async (limit = 50, skip = 0) => {
    const response = await api.get(`/exercises?limit=${limit}&skip=${skip}`);
    return response.data;
  },
  
  delete: async (exerciseId) => {
    const response = await api.delete(`/exercises/${exerciseId}`);
    return response.data;
  },
};

// ============== HEALTH VITALS API ==============
export const healthVitalsAPI = {
  create: async (vitalData) => {
    const response = await api.post('/health-vitals', vitalData);
    return response.data;
  },
  
  getAll: async (limit = 50, skip = 0) => {
    const response = await api.get(`/health-vitals?limit=${limit}&skip=${skip}`);
    return response.data;
  },
  
  delete: async (vitalId) => {
    const response = await api.delete(`/health-vitals/${vitalId}`);
    return response.data;
  },
};

// ============== COMMUNITY API ==============
export const communityAPI = {
  create: async (postData) => {
    const response = await api.post('/community', postData);
    return response.data;
  },
  
  getAll: async (category = null, limit = 50, skip = 0) => {
    let url = `/community?limit=${limit}&skip=${skip}`;
    if (category) url += `&category=${category}`;
    const response = await api.get(url);
    return response.data;
  },
  
  like: async (postId) => {
    const response = await api.post(`/community/${postId}/like`);
    return response.data;
  },
  
  comment: async (postId, content) => {
    const response = await api.post(`/community/${postId}/comment`, { content });
    return response.data;
  },
  
  delete: async (postId) => {
    const response = await api.delete(`/community/${postId}`);
    return response.data;
  },
};

// ============== CARE TEAM API ==============
export const careTeamAPI = {
  create: async (memberData) => {
    const response = await api.post('/care-team', memberData);
    return response.data;
  },
  
  getAll: async () => {
    const response = await api.get('/care-team');
    return response.data;
  },
  
  delete: async (memberId) => {
    const response = await api.delete(`/care-team/${memberId}`);
    return response.data;
  },
};

// ============== INSIGHTS API ==============
export const insightsAPI = {
  getAll: async (limit = 10) => {
    const response = await api.get(`/insights?limit=${limit}`);
    return response.data;
  },
  
  generate: async () => {
    const response = await api.post('/insights/generate');
    return response.data;
  },
};

// ============== FOOD API (OpenFoodFacts) ==============
export const foodAPI = {
  getByBarcode: async (barcode) => {
    const response = await api.get(`/food/barcode/${barcode}`);
    return response.data;
  },
  
  scanBarcode: async (barcode) => {
    const response = await api.get(`/food/barcode/${barcode}`);
    return response.data;
  },
  
  search: async (query, limit = 10) => {
    const response = await api.get(`/food/search?query=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data;
  },
};

// ============== AI API ==============
export const aiAPI = {
  chat: async (text, sessionId = null) => {
    const response = await api.post('/ai/chat', { 
      text, 
      session_id: sessionId 
    });
    return response.data;
  },
  
  analyzeMeal: async (mealName, carbs, sugar) => {
    const response = await api.post('/ai/meal-analysis', {
      meal_name: mealName,
      carbs,
      sugar
    });
    return response.data;
  },
  
  analyzeMealPhoto: async (imageBase64, notes = '') => {
    const response = await api.post('/ai/analyze-meal-photo', {
      image_base64: imageBase64,
      notes
    });
    return response.data;
  },
  
  predictGlucose: async () => {
    const response = await api.post('/ai/predict-glucose', {});
    return response.data;
  },
};

// ============== SUBSCRIPTION API ==============
export const subscriptionAPI = {
  getStatus: async () => {
    const response = await api.get('/user/subscription-status');
    return response.data;
  },
};

// ============== JOURNAL API ==============
export const journalAPI = {
  create: async (entryData) => {
    const response = await api.post('/journal', entryData);
    return response.data;
  },
  
  getAll: async (limit = 30, skip = 0) => {
    const response = await api.get(`/journal?limit=${limit}&skip=${skip}`);
    return response.data;
  },
};

// ============== SHOP API ==============
export const shopAPI = {
  getAll: async (category = null, limit = 50, skip = 0) => {
    let url = `/shop?limit=${limit}&skip=${skip}`;
    if (category) url += `&category=${category}`;
    const response = await api.get(url);
    return response.data;
  },
  
  getOne: async (productId) => {
    const response = await api.get(`/shop/${productId}`);
    return response.data;
  },
};

// ============== ANALYTICS API ==============
export const analyticsAPI = {
  getSummary: async () => {
    const response = await api.get('/analytics/summary');
    return response.data;
  },
  
  getGlucoseTrend: async (days = 7) => {
    const response = await api.get(`/analytics/glucose-trend?days=${days}`);
    return response.data;
  },
};

// ============== SEED API (Development) ==============
export const seedAPI = {
  seedRecipes: async () => {
    const response = await api.post('/seed-recipes');
    return response.data;
  },
  
  seedProducts: async () => {
    const response = await api.post('/seed-products');
    return response.data;
  },
};

export default api;
